// A Container for a value that may or may not exist
export class Option<T = unknown> {
	protected constructor() {}
	
	// Broadens the typing to be Option.
	public get infer(): Option<T> {
		return this;
	}
	
	public isSome(): this is Some<T> {
		return Option.isSome(this);
	}
	
	public isNone(): this is None<T> {
		return Option.isNone(this);
	}
	
	public map<R>(fn: SomeCallback<T, R>): Option<R> {
		return this.isSome() ? Option.of(fn(this.value)) : Option.none<R>();
	}
	
	public flatMap<R>(fn: SomeCallback<T, Option<R>>): Option<R> {
		return this.isSome() ? fn(this.value) : Option.none<R>();
	}
	
	public filter(fn: SomeCallback<T, boolean>): Option<T> {
		return this.isSome() && fn(this.value) ? this : Option.none<T>();
	}
	
	public nullable(): Option<T | null> {
		return this.isSome() ? Option.of(this.value) : Option.none<T>();
	}
	
	public fork<S, E>({some, none}: { some: SomeCallback<T, OptionOr<S>>, none: NoneCallback<OptionOr<E>>}): Option<S | E> {
		let result: OptionOr<S | E>;
		if (this.isSome()) {
			result = some(this.value);
		}
		else {
			result = none();
		}
		
		if (result instanceof Option) {
			return result;
		}
		
		return Option.of(result);
	}
	
	public nonNullable(): Option<NonNullable<T>> {
		if (this.isSome()) {
			return Option.nonNull(this.value);
		}
		
		return Option.none<NonNullable<T>>();
	}
	
	
	public orElse<R>(fn: NoneCallback<R>): Some<T | R> {
		if (this.isSome()) {
			return this;
		}
		
		return Option.of(fn());
	}
	
	public orThrow(toThrow?: ThrowType): Some<T> {
		if (this.isSome()) {
			return this;
		}
		
		this.throwingRelative(toThrow);
	}
	
	public getOrProvider<R>(fn: NoneCallback<R>): T | R {
		if (this.isSome()) {
			return this.value;
		}
		
		return fn();
	}
	
	public getOrElse<R>(defaultValue: R): T | R {
		return this.isSome() ? this.value : defaultValue;
	}
	
	public getOrNull(): T | null {
		return this.isSome() ? this.value : null;
	}
	
	public whenSome(fn: SomeCallback<T, void>): void {
		if (this.isSome()) {
			fn(this.value);
		}
	}
	
	public whenNone(fn: NoneCallback<void>): void {
		if (this.isNone()) {
			fn();
		}
	}
	
	public match<R1, R2>({ some, none }: { some: SomeCallback<T, R1>, none: NoneCallback<R2> }): R1 | R2 {
		if (this.isSome()) {
			return some(this.value);
		}
		else {
			return none();
		}
	}
	
	/**
	 * Returns the value if present, otherwise throws an error. If an error is provided, it will be thrown.
	 * If a string is provided, it will be used as the error message.
	 */
	public getOrThrow(toThrow?: ThrowType): T {
		if (this.isSome()) {
			return this.value;
		}
		
		this.throwingRelative(toThrow);
	}
	
	public castAsType<R extends T>(predicate: (x: T) => x is R): Option<R> {
		if (this.isSome()) {
			return predicate(this.value) ? Option.of(this.value) : Option.none<R>();
		}
		
		return Option.none<R>();
	}
	
	public toString(): string {
		return this.isSome() ? `Some(${this.value})` : 'None';
	}
	
	private throwingRelative(toThrow: ThrowType): never {
		if (typeof toThrow === 'function') {
			throw toThrow();
		}
		else if (typeof toThrow === 'string') {
			throw new Error(toThrow);
		}
		else if (toThrow == null) {
			throw new Error('No value present');
		}
		else {
			throw toThrow;
		}
	}
}

export namespace Option {
	export function of<T>(val: T): Some<T> {
		return new Some(val);
	}
	
	export function none<T>(): None<T> {
		return new None<T>();
	}
	
	export function nonNull<T>(val: T): Option<NonNullable<T>> {
		return val != null ? of<NonNullable<T>>(val as NonNullable<T>) : none();
	}
	
	export function safeOption<T>(opt: Option<T> | undefined | null): Option<T> {
		return opt ?? none<T>();
	}
	
	export function isSome<T>(opt: Option<T>): opt is Some<T> {
		return opt instanceof Some;
	}
	
	export function isNone<T>(opt: Option<T>): opt is None<T> {
		return opt instanceof None;
	}
	
	/**
	 * Creates an array of values from the provided options. If an option is a Some, its value is added to the array.
	 * @param options Options or values
	 */
	export function arrayOfSome<T>(...options: Array<T | Option<T>>): T[] {
		return options.reduce((arr, opt) => {
			if (opt instanceof Option) {
				if (opt.isSome()) {
					arr.push(opt.value);
				}
			}
			else {
				arr.push(opt);
			}
			
			return arr;
		}, [] as Array<T>);
	}
	
	export function first<T>(...options: Option<T>[]): Option<T> {
		for (const op of options) {
			if (op.isSome()) {
				return op;
			}
		}
		
		return Option.none<T>();
	}
}

// An Option value that exists
export class Some<T = unknown> extends Option<T> {
	constructor(
		public readonly value: T
	) {
		super();
	}
}

// An Option value that does not exist
export class None<T = unknown> extends Option<T> {
	constructor() {
		super();
	}
}

type OptionOr<T> = T | Option<T>;
type SomeCallback<T, R> = (val: T) => R;
type NoneCallback<R> = () => R;
type ThrowType = string | (() => unknown) | (unknown & {}) | null | undefined | Error;

export { Option as default, Option as OptionType };